import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Injectable } from '@angular/core';
import { from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TokenService } from '../services/token.service';
import { IdentityService } from '../services/identity.service';
import { LogHelper } from '../helpers/log.helper';
import { ErrorHelper } from '../helpers/error.helper';
import { PkceHelper } from '../helpers/pkce.helper';
import { CanActivate } from '../interfaces/can-activate.interface';
import { GuardResult, MaybeAsync } from '../types/internal-types';
import { OptionsService } from '../services/options.service';

@Injectable({
	providedIn: 'root'
})
export class IdentityAuthCallbackGuard implements CanActivate {
	constructor(
		private readonly router: Router,
		private readonly tokenService: TokenService,
		private readonly identityService: IdentityService,
		private readonly os: OptionsService
	) {}

	canActivate(route: ActivatedRouteSnapshot): MaybeAsync<GuardResult> {
		/**
		 * display an error message on the page and return false to stop further
		 * navigation; text (not HTML) because the message may embed attacker-controlled
		 * JWT claim values from the callback query param (innerHTML would be XSS)
		 */
		const denyWithError: (error: unknown) => boolean = (error: unknown): boolean => {
			const routerOutlet: Element = document.body.querySelector('router-outlet');

			if (routerOutlet) {
				routerOutlet.textContent = ErrorHelper.extractMessage(error);
			}

			LogHelper.error(error);

			return false;
		};

		/**
		 * store the token pair and hand the router a UrlTree for the redirect.
		 * We return an UrlTree instead of calling navigateByUrl imperatively:
		 * navigateByUrl from inside a guard whose result depends on that navigation
		 * deadlocks — the outer (auth-callback) navigation waits on this guard, the
		 * guard waits on the nested navigation, and the router runs one navigation
		 * at a time. A UrlTree folds the redirect into THIS navigation instead.
		 */
		const applyTokens: (accessToken: string, refreshToken: string) => UrlTree = (
			accessToken: string,
			refreshToken: string
		): UrlTree => {
			this.tokenService.setAccessToken(accessToken);

			this.tokenService.setRefreshToken(refreshToken);

			return this.router.parseUrl(route.queryParams['redirect_path'] || '/');
		};

		const code: string = route.queryParams['code'];

		/**
		 * bare launch: IDMS had a session but was launched with no redirect_path, so
		 * it minted no code and sent us here with `code=none`. Re-login *explicitly*
		 * with a redirect_path so IDMS mints a real code. The redirect_path MUST be
		 * non-empty, or IDMS returns `code=none` again (infinite loop) — a bare launch
		 * has no deep-link target, so falls back to the app root, exactly as the token
		 * path below does for navigation. A fresh PKCE challenge is sent so IDMS can
		 * mint a code bound to this client.
		 */
		if (code === 'none') {
			return from(
				PkceHelper.buildLoginRedirectUrl(this.os.options, route.queryParams['redirect_path'] || '/')
			).pipe(
				map(url => {
					window.location.href = url;

					return false;
				})
			);
		}

		/**
		 * code-enabled app: exchange the one-time code for the same token pair the
		 * legacy path receives, store it, and redirect to redirect_path.
		 */
		if (code) {
			return this.identityService.redeemCode(code).pipe(
				map(response => applyTokens(response.access_token, response.refresh_token)),
				catchError((error: unknown) => of(denyWithError(error)))
			);
		}

		/**
		 * legacy tokens-in-URL flow: save access and refresh tokens and redirect
		 * either to root or to the provided "redirect_path".
		 */
		if (!route.queryParams['access_token'] || !route.queryParams['refresh_token']) {
			return denyWithError('No access or refresh token provided');
		}

		/**
		 * setAccessToken/setRefreshToken decode+validate and throw synchronously on
		 * an expired/invalid token — display the message and stop navigation.
		 */
		try {
			return applyTokens(route.queryParams['access_token'], route.queryParams['refresh_token']);
		} catch (error) {
			return denyWithError(error);
		}
	}
}
